import { Form, Formik, FormikProps } from "formik";
import moment from "moment";
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useRef,
  useState
} from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { AppContext } from "../../../appContext/AppContext";
import { KEY_LOCALSTORAGE } from "../../../auth/core/_consts";
import FormInPhieuDialog from "../../../component/button-in-phieu/components/PrintDialog";
import { ConfirmDialog } from "../../../component/ConfirmDialog";
import { IInPhieuParams } from "../../../models/params";
import { CODE, RESPONSE_MESSAGE } from "../../../utils/Constant";
import { formatDateDTO, getErrorMessage } from "../../../utils/FormatUtils";
import { localStorageItem } from "../../../utils/LocalStorage";
import { PhanHeTiepDonContext } from "../../contexts/PhanHeTiepDonContext";
import {
  createDSChiDinhDV,
  createDSChiDinhDVTiepDon,
  updateDSChiDinhDV,
  validateChiDinhDV
} from "../../services/ChiDinhDVService";
import { inPhieuChiDinhChung } from "../../services/PrintExportService";
import FormChiDinhDichVu from "./FormChiDinhDichVu";
import { ServiceSplitHelper } from "../../utils/ServiceSplitHelper";

interface Props {
  show?: boolean;
  handleClose: Dispatch<SetStateAction<boolean>>;
  dieuTri?: boolean;
  orderGroupId?: string;
  generalInfo?: any;
  onUpdate?: () => void;
}

const PageChiDinhDichVu: FC<Props> = (props) => {
  let { show = true, handleClose, orderGroupId, generalInfo = {}, onUpdate } = props;
  const intl = useIntl();
  const { setIsLoading } = useContext(AppContext);
  const { benhNhanInfo, setBenhNhanInfo, statusChangedEventFunc } =
    useContext(PhanHeTiepDonContext);
  const currentUser = localStorageItem.get(KEY_LOCALSTORAGE.CURRENT_USER);
  let { patient, visit, encounter, orderId } =
    benhNhanInfo?.thongTinKhamBenh || generalInfo;
  const formikRef = useRef<FormikProps<any>>(null);
  const [shouldOpenConfirmDialog, setShouldOpenConfirmDialog] =
    useState<boolean>(false);
  const [validateMessage, setValidateMessage] = useState<string>("");
  const [validateDVMessage, setValidateDVMessage] = useState<any>(null);
  const [orderGroupIds, setOrderGroupIds] = useState<string[]>([]);
  const [sessionCode, setSessionCode] = useState<string>("");
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
  const isSaveAndPrint = useRef<boolean>(false);
  const userMetaData = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA);

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    } else {
      setBenhNhanInfo({
        ...benhNhanInfo,
        shouldUpdate: !benhNhanInfo?.shouldUpdate,
      });
      statusChangedEventFunc?.();
    }
  };

const handleSubmit = async (values: any) => {
    if (!(values?.orders ?? values?.ordersDto ?? []).length) {
      toast.warn("Chưa có dịch vụ được chỉ định cho phiếu này");
      return;
    }

    let createData, updateData, dataTiepDon;
    let _ordersDto = [];
    if (values?.allowDisableRules) {
      createData = values;
      updateData = values;
      dataTiepDon = values;
      _ordersDto = values?.ordersDto;
    } else {
      let encounterDiagnosisDto = {
        ...values?.encounterDiagnosis,
        patientId: patient?.id,
      };

      // Step 1: Expand rows that have _splitItems OR quantity > 1
      const expandedOrders = (values?.orders || []).flatMap((order: any) => {
        // Priority 1: Row đã edit popup → Use split items
        if (order._hasSplitItems && order._splitItems) {
          console.log('Expanding row with _splitItems:', {
            code: order.code,
            splitItemsCount: order._splitItems.length,
          });
          return order._splitItems;
        }

        // Priority 2: Row có quantity > 1 NHƯNG chưa edit popup
        // → Tạo split items on-the-fly
        const quantity = order?.attributes?.svcQuantity || 1;
        if (quantity > 1) {
          console.log('Auto-expanding row with quantity > 1:', {
            code: order.code,
            quantity,
          });
          const splitItems = ServiceSplitHelper.createSplitItems(order, quantity);
          return splitItems;
        }

        // Priority 3: Regular row (quantity = 1) → Keep as is
        return [order];
      });

      // Step 2: Map to ordersDto (following old code pattern)
      let ordersDto = expandedOrders?.map((item: any) => {
        // Extract id BEFORE cleaning (cleanForBackend removes id)
        const { id, ...itemValues } = item ?? {};

        // Clean split metadata from itemValues
        const cleanedItem = ServiceSplitHelper.cleanForBackend([itemValues])[0];

        // DTO structure matches backend expectations
        const dto = {
          ...cleanedItem,
          // termId: Map service id to termId (required by backend)
          termId: id,
          fulfillDeptCode: item?.fulfillDeptCode,
          fulfillDeptName: item?.fulfillDeptName,
          // Map visitObjectType to flat fields
          objectTypeId: item?.visitObjectType?.id,
          objectTypeCode: item?.visitObjectType?.code,
          objectTypeName: item?.visitObjectType?.name,
          // Ensure boolean
          isOwnExpenses: Boolean(item?.isOwnExpenses),
        };

        return dto;
      });
      let userInfo = {
        indicationPerson: currentUser?.name,
        requestDeptName: currentUser?.departments?.[0]?.name || null,
      };
      _ordersDto = ordersDto;

      createData = values?.allowDisableRules ? values : {
        ...userInfo,
        orderId,
        encounterId: encounter?.id,
        patientId: patient?.id,
        visitId: visit?.id,
        indicationTime: values?.indicationTime || formatDateDTO(new Date()),
        encounterDiagnosisDto,
        ordersDto,
        pid: userMetaData?.personId,
      };
      updateData = {
        encounterDiagnosis: encounterDiagnosisDto,
        ordersDto,
        orderGroupId,
        orderId,
        indicationTime: values?.indicationTime,
      };
      dataTiepDon = {
        encounterId: encounter?.id,
        indicationTime: values?.indicationTime || formatDateDTO(new Date()),
        encounterDiagnosis: encounterDiagnosisDto,
        orders: ordersDto,
      };
    }
    let validateData = {
      ordersDto: _ordersDto,
      visitId: visit?.id,
      indicationTime: values?.indicationTime || formatDateDTO(new Date()),
      clinicalOrderId: orderId,
    };
    
    try {
      setIsLoading(true);
      if (!validateMessage && !generalInfo?.forTiepDon) {
        let { data: validateResponse } = await validateChiDinhDV(validateData);
        if (validateResponse?.code === CODE.SUCCESS) {
          setValidateMessage("");
        } else {
          let errorMessage = getErrorMessage(validateResponse);
          const errorMessageFormatted = Array.isArray(errorMessage)
            ? errorMessage.map((error: string) => <div key={error}>{error}</div>)
            : errorMessage;
          if (validateResponse?.code === CODE.ERROR_CODE_400 || Array.isArray(errorMessage)) {
            setValidateMessage(errorMessageFormatted);
            setShouldOpenConfirmDialog(true);
          } else {
            toast.warning(errorMessage);
            handleClose?.(false)
            handleUpdate();
          }
          return;
        }
      }
      let { data } = generalInfo?.forTiepDon
        ? await createDSChiDinhDVTiepDon(dataTiepDon, handleSubmit)
        : orderGroupId
          ? await updateDSChiDinhDV(updateData, handleSubmit)
          : await createDSChiDinhDV(createData, handleSubmit);
      if (CODE.SUCCESS === data?.code) {
        setOrderGroupIds(data?.data?.orderGroupIds || data?.data);
        setSessionCode(data?.data?.sessionCode);
        handleUpdate();
        toast.success("Cập nhật thành công");
        isSaveAndPrint.current && setOpenPrintDialog(true);
        !isSaveAndPrint.current && handleClose(false);
      } else {
        setShouldOpenConfirmDialog(false);
        const errorMessage = getErrorMessage(data);
        const errorMessageFormatted = Array.isArray(getErrorMessage(data))
          ? errorMessage.map((error: string) => <div key={error}>{error}</div>)
          : errorMessage;

        setValidateDVMessage({
          content: (
            <div>
              {errorMessageFormatted}
            </div>
          ),
        });
      }
    } catch (error) {
      console.error(error);
      toast.warning("Xảy ra lỗi, vui lòng thử lại!");
    } finally {
      setIsLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    encounterDiagnosis: Yup.object().shape({
      diagnosisNonCoded: Yup.string()
        .nullable()
        .when("provisionalDiagnosis", (value, schema) => {
          if (!value) {
            return schema.required(
              intl.formatMessage({ id: "VALIDATION.REQUIRE" })
            );
          }
          return schema; // đảm bảo trả về schema nếu điều kiện không khớp
        }),
      // provisionalDiagnosisName: Yup.string()
      //   .nullable()
      //   .required(intl.formatMessage({ id: "VALIDATION.REQUIRE" })),
    }),
    indicationTime: Yup.date()
      .nullable()
      .when([], (schema) => {
        if (visit?.dateStarted) {
          return schema.min(
            new Date(visit.dateStarted),
            `Ngày y lệnh không được nhỏ hơn ngày vào viện (${moment(
              visit.dateStarted
            ).format("DD/MM/YYYY - HH:mm")})`
          );
        }
        return schema;
      })
  });

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
		<Modal
			className="page-full"
			fullscreen
			dialogClassName="h-modal"
			size="xl"
			show={show}
			animation={false}
			centered
			backdropClassName="spaces top-50"
		>
			<Formik<any>
        initialValues={{}}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				innerRef={formikRef}
			>
				<Form id="form-thuoc" className="spaces h-calc-vh-50" onKeyDown={handleKeyPress}>
          <FormChiDinhDichVu
            handleClose={handleClose}
            orderGroupId={orderGroupId}
            isSaveAndPrint={isSaveAndPrint}
            generalInfo={generalInfo}
          />

					{shouldOpenConfirmDialog && (
						<ConfirmDialog
							className="z-index-1060"
							show={shouldOpenConfirmDialog}
							title="Thông báo"
							message={validateMessage}
							yes="Có"
							close="Không"
							onCloseClick={() => {
								setValidateMessage('');
								setShouldOpenConfirmDialog(false);
							}}
							onYesClick={() => {
                setShouldOpenConfirmDialog(false);
                formikRef.current?.submitForm()
              }}
						/>
					)}

          {openPrintDialog && orderGroupIds?.length && sessionCode && (
            <FormInPhieuDialog<IInPhieuParams>
              show={openPrintDialog}
              onHide={() => {
                handleClose(false);
                setOpenPrintDialog(false);
              }}
              fetchExport={inPhieuChiDinhChung}
              params={{
                visitId: visit?.id,
                patientId: patient?.id,
                orderGroupId: orderGroupIds?.[0],
                sessionCode
              }}
            />
          )}
          {validateDVMessage && (
						<ConfirmDialog
							title="Thông báo"
							yes="Đóng"
							onYesClick={() => setValidateDVMessage(null)}
							message={validateDVMessage?.content}
							show={Boolean(validateDVMessage)}
							onCloseClick={() => setValidateDVMessage(null)}
						/>
					)}
				</Form>
			</Formik>
		</Modal>
  );
};

export default PageChiDinhDichVu;
